/**
 * Datos legales de la explotación (tabla `farm_legal_profiles`, CQ1 del
 * cuaderno de campo): leerlos y guardarlos desde Ajustes → Empresa.
 *
 * Una fila con `location_id` NULL es la de toda la empresa; una finca de OTRO
 * titular lleva la suya (D14: el cuaderno es por titular). Leen el admin y el
 * encargado (el trabajador y el perito no: hay NIF y teléfonos, E11) y escribe
 * solo el admin; lo impone la RLS por `user_roles` (E1/E6).
 *
 * E11: el NIF es un dato personal. Los errores de la base se relanzan SIN sus
 * `details` (un fallo de CHECK de PostgREST trae la fila entera, NIF incluido, y
 * una promesa rechazada acaba en `error_logs`): solo viajan el código y la tabla.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Tables } from "@/integrations/supabase/types";

export type FilaDatosLegales = Tables<"farm_legal_profiles">;

export const DATOS_EXPLOTACION_KEY = "farm-legal-profiles";

/** Error de la base sin valores dentro (E11). */
export function errorSinDatos(tabla: string, error: { code?: string | null } | null | undefined): Error {
  return new Error(`${tabla}: ${error?.code ?? "error"}`);
}

export function useDatosExplotacion() {
  const { company } = useAuth();
  return useQuery({
    queryKey: [DATOS_EXPLOTACION_KEY, company?.id],
    queryFn: async (): Promise<FilaDatosLegales[]> => {
      if (!company?.id) return [];
      const { data, error } = await supabase.from("farm_legal_profiles").select("*").eq("company_id", company.id);
      if (error) throw errorSinDatos("farm_legal_profiles", error);
      return data ?? [];
    },
    enabled: !!company?.id,
  });
}

export interface GuardarDatosInput {
  /** La fila que se edita, si ya existe. */
  id?: string | null;
  /** null = la de toda la empresa. */
  locationId: string | null;
  /** Lo que devuelve `rowFromForm`. */
  valores: Record<string, unknown>;
}

export function useGuardarDatosExplotacion() {
  const { company } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, locationId, valores }: GuardarDatosInput) => {
      if (!company?.id) throw new Error("farm_legal_profiles: sin empresa");
      // La empresa y la finca no las decide el formulario: se fijan aquí.
      const fila = { ...valores, company_id: company.id, location_id: locationId };
      const { error } = id
        ? await supabase.from("farm_legal_profiles").update(fila).eq("id", id).eq("company_id", company.id)
        : await supabase.from("farm_legal_profiles").insert(fila as FilaDatosLegales);
      if (error) throw errorSinDatos("farm_legal_profiles", error);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [DATOS_EXPLOTACION_KEY, company?.id] }),
  });
}

/** Quitar la fila de una finca: vuelve a ser del titular de la empresa. La de la empresa no se borra. */
export function useQuitarTitularFinca() {
  const { company } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (!company?.id) throw new Error("farm_legal_profiles: sin empresa");
      const { error } = await supabase
        .from("farm_legal_profiles")
        .delete()
        .eq("id", id)
        .eq("company_id", company.id)
        .not("location_id", "is", null);
      if (error) throw errorSinDatos("farm_legal_profiles", error);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [DATOS_EXPLOTACION_KEY, company?.id] }),
  });
}
